import { useEffect, useRef } from "react";
import Cookies from "js-cookie";

var FLUSH_INTERVAL = 5000;
var MAX_BUFFER_SIZE = 10;
var COLLECT_URL = "/api/v1/analytics/collect/";
var SESSION_COOKIE = "glavnoe_sid";
var SESSION_TTL_DAYS = 1 / 48; // 30 минут в днях

function generateSessionId() {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
        var r = (Math.random() * 16) | 0;
        var v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

var COOKIE_OPTS = {
    expires: SESSION_TTL_DAYS,
    path: "/",
    sameSite: "Lax",
    secure: window.location.protocol === "https:"
};
var UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

function getOrCreateSessionId() {
    var sid = Cookies.get(SESSION_COOKIE);
    if (!sid || !UUID_RE.test(sid)) {
        sid = generateSessionId();
    }
    Cookies.set(SESSION_COOKIE, sid, COOKIE_OPTS);
    return sid;
}

function refreshSessionCookie(sid) {
    Cookies.set(SESSION_COOKIE, sid, COOKIE_OPTS);
}

function getVisitorId() {
    return Cookies.get("glavnoe_uid") || "";
}

function AnalyticsTracker(props) {
    var newsId = props.newsId || 0;
    var streamId = props.streamId || 0;
    var isFull = !!props.isFull;
    var isPreview = !isFull;

    var bufferRef = useRef([]);
    var timerRef = useRef(null);
    var scrollDepthRef = useRef(0);
    var sentMilestonesRef = useRef({});
    var startTimeRef = useRef(Date.now());
    var sessionIdRef = useRef(props.sessionId || getOrCreateSessionId());
    var visitorIdRef = useRef(props.visitorId || "");
    var utmParamsRef = useRef(props.utmParams || null);
    var didLeaveRef = useRef(false);

    function buildEvent(type) {
        var event = {
            type: type,
            news_id: newsId,
            stream_id: streamId,
            is_preview: isPreview,
            is_full: isFull,
            visitor_id: visitorIdRef.current || getVisitorId(),
            session_id: sessionIdRef.current,
            url: window.location.href,
            scroll_depth: scrollDepthRef.current,
            time_on_page: Math.round((Date.now() - startTimeRef.current) / 1000),
        };
        if (props.device) { event.device = props.device; }
        if (props.browser) { event.browser = props.browser; }
        if (utmParamsRef.current) {
            var u = utmParamsRef.current;
            event.utm_source = u.utm_source || "";
            event.utm_medium = u.utm_medium || "";
            event.utm_campaign = u.utm_campaign || "";
            event.utm_term = u.utm_term || "";
            event.utm_content = u.utm_content || "";
            event.subid1 = u.subid1 || "";
            event.subid2 = u.subid2 || "";
            event.subid3 = u.subid3 || "";
            event.subid4 = u.subid4 || "";
            event.subid5 = u.subid5 || "";
        }
        return event;
    }

    function pushEvent(type, extra) {
        var event = buildEvent(type);
        if (extra) {
            Object.keys(extra).forEach(function (k) { event[k] = extra[k]; });
        }
        bufferRef.current.push(event);

        if (bufferRef.current.length >= MAX_BUFFER_SIZE) {
            flush();
        }
    }

    function flush() {
        var events = bufferRef.current.splice(0);
        if (events.length === 0) return;

        refreshSessionCookie(sessionIdRef.current);

        try {
            var xhr = new XMLHttpRequest();
            xhr.open("POST", COLLECT_URL, true);
            xhr.setRequestHeader("Content-Type", "application/json");
            xhr.send(JSON.stringify({ events: events }));
        } catch (e) {
            // silently fail
        }
    }

    function flushBeacon() {
        if (didLeaveRef.current) return;
        didLeaveRef.current = true;

        var events = bufferRef.current.splice(0);
        events.push(buildEvent("leave"));

        if (navigator.sendBeacon) {
            navigator.sendBeacon(
                COLLECT_URL,
                new Blob([JSON.stringify({ events: events })], { type: "application/json" })
            );
        } else {
            try {
                var xhr = new XMLHttpRequest();
                xhr.open("POST", COLLECT_URL, false);
                xhr.setRequestHeader("Content-Type", "application/json");
                xhr.send(JSON.stringify({ events: events }));
            } catch (e) {
                // silently fail
            }
        }
    }

    useEffect(function () {
        // Reset state for new page
        scrollDepthRef.current = 0;
        sentMilestonesRef.current = {};
        startTimeRef.current = Date.now();
        sessionIdRef.current = props.sessionId || getOrCreateSessionId();
        if (props.sessionId) {
            refreshSessionCookie(props.sessionId);
        }
        visitorIdRef.current = props.visitorId || "";
        utmParamsRef.current = props.utmParams || null;
        didLeaveRef.current = false;

        // Periodic flush
        timerRef.current = setInterval(flush, FLUSH_INTERVAL);

        // Scroll depth tracking via IntersectionObserver
        var observers = [];
        var markers = [25, 50, 75, 100];

        if (typeof IntersectionObserver !== "undefined") {
            markers.forEach(function (pct) {
                var marker = document.createElement("div");
                marker.style.width = "1px";
                marker.style.height = "1px";
                marker.style.position = "absolute";
                marker.style.left = "0";
                marker.style.top = pct + "%";
                marker.style.pointerEvents = "none";

                var contentEl = document.getElementById("content") || document.body;
                contentEl.style.position = "relative";
                contentEl.appendChild(marker);

                var observer = new IntersectionObserver(function (entries) {
                    if (entries[0] && entries[0].isIntersecting) {
                        // Send events for all unsent milestones up to this one
                        markers.forEach(function (m) {
                            if (m <= pct && !sentMilestonesRef.current[m]) {
                                sentMilestonesRef.current[m] = true;
                                pushEvent("scroll", { scroll_depth: m });
                            }
                        });
                        if (pct > scrollDepthRef.current) {
                            scrollDepthRef.current = pct;
                        }
                        observer.disconnect();
                    }
                }, { threshold: 0 });

                observer.observe(marker);
                observers.push({ observer: observer, marker: marker });
            });
        }

        // Leave tracking
        function handleVisibilityChange() {
            if (document.visibilityState === "hidden") {
                flushBeacon();
            } else {
                didLeaveRef.current = false;
            }
        }

        function handleBeforeUnload() {
            flushBeacon();
        }

        document.addEventListener("visibilitychange", handleVisibilityChange);
        window.addEventListener("beforeunload", handleBeforeUnload);

        return function () {
            clearInterval(timerRef.current);
            flush();
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            window.removeEventListener("beforeunload", handleBeforeUnload);
            observers.forEach(function (o) {
                o.observer.disconnect();
                if (o.marker.parentNode) o.marker.parentNode.removeChild(o.marker);
            });
        };
    }, [newsId, streamId, isFull]);

    return null;
}

export default AnalyticsTracker;
